import {
  ApolloClient,
  InMemoryCache,
  TypePolicies,
  ApolloLink,
  HttpLink,
} from "@apollo/client";
import { PersistedQueryLink } from "@apollo/client/link/persisted-queries";
import { relayStylePagination } from "@apollo/client/utilities";
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs";
import { sha256 } from "crypto-hash";

import { coalesce } from "@kazoohr/helpers";

import { customClientFetch } from "./customClientFetch";

export const mergeIncoming = (_: any, incoming: any) => incoming;

// New versions of Apollo don't add things to the normalized cache if the
// objects don't have IDs. (How could it?) So we have to tell Apollo where it's
// acceptable to add things to the cache.
export const applicationTypePolicies: TypePolicies = {
  directory_User: {
    fields: {
      points: { merge: true },
    },
  },
  admin_SubItem: { keyFields: ["id", "url"] },
  admin_NavigationItem: { keyFields: ["id", "url"] },
  admin_EmbeddedRoute: { keyFields: ["route", "embedUrl"] },
  team_TeamMember: { keyFields: ["login"] },
  /* eslint-disable @typescript-eslint/naming-convention  */
  activityFeed_RecognitionActivity: {
    keyFields: ["id", "timestamp"],
    fields: { id: { merge: mergeIncoming } },
  },
  /* eslint-disable @typescript-eslint/naming-convention  */
  activityFeed_activities: {
    keyFields: ["id", "timestamp"],
    fields: { id: { merge: mergeIncoming } },
  },
  Query: {
    fields: {
      admin_navigation: { merge: true },
      layout_companyBranding: { merge: true },
      profile_profileVisibility: { merge: true },
      activityFeed_activities: { merge: mergeIncoming },
    },
  },
};

export const apiTypePolicies: TypePolicies = {
  Query: {
    fields: {
      alerts: relayStylePagination(["filter"]),
      incentivesCatalogs: relayStylePagination(["filter", "sort"]),
      nominations: relayStylePagination(["filter", "sort"]),
    },
  },
  /**
   * We need these to cache things properly. The prompts are tied to the main entity (I.E. Feedback Form, not Feedback).
   * So, when you cache them without surveyId, they overwrite each other when clicking around, especially in Check-Ins.
   */
  SurveyShortAnswerPrompt: {
    keyFields: ["id", "surveyId"],
  },
  SurveyRatingScalePrompt: {
    keyFields: ["id", "surveyId"],
  },
  SurveyMultipleChoicePrompt: {
    keyFields: ["id", "surveyId"],
  },
  SurveyElement: {
    keyFields: ["id", "surveyId"],
  },
};

function getHttpLink(uri: string, correlationId: string): ApolloLink {
  const headers = {
    "X-Request-ID": correlationId,
    "Apollo-Require-Preflight": "true",
  };
  const mutationLinkOpts = { uri, headers };
  const queryLinkOpts = { uri, headers, fetch: customClientFetch };

  const persistedQueryLink = new PersistedQueryLink({ sha256 });
  const mutationHttpLink = new HttpLink(mutationLinkOpts);
  const queryHttpLink = new HttpLink(queryLinkOpts);

  const uploadLink = new UploadHttpLink(queryLinkOpts);
  const queryLink = persistedQueryLink.concat(queryHttpLink);
  const mutationLink = persistedQueryLink.concat(mutationHttpLink);

  const nonUploadLink = ApolloLink.split(
    (op) =>
      op.query.definitions.some(
        (d: any) =>
          d.kind === "OperationDefinition" && d.operation === "mutation"
      ),
    mutationLink,
    queryLink
  );

  return ApolloLink.split(
    // istanbul ignore next - apollo internals
    ({ variables }) => hasFiles(variables),
    // `apollo-upload-client` bundles its own copy of the `@apollo/client` types,
    // so its `ApolloLink` is a distinct nominal type even though at runtime this
    // is a real `ApolloLink`.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    uploadLink as unknown as ApolloLink,
    nonUploadLink
  );
}

/**
 * Recursively check if any of the variables contain a file. Complex because
 * there are a lot of early returns to ensure the complex stuff doesn't run
 * unless necessary.
 */
export function hasFiles(variables?: any): boolean {
  if (!variables || typeof variables !== "object") {
    return false;
  }
  const keys = Object.keys(variables);
  for (const key of keys) {
    const node = variables[key];
    if (!node || typeof node !== "object") {
      continue;
    }

    if (
      (typeof File !== "undefined" && node instanceof File) ||
      (typeof Blob !== "undefined" && node instanceof Blob)
    ) {
      return true;
    }

    if (hasFiles(node)) {
      return true;
    }
  }

  return false;
}

/**
 * Apollo 4 flipped the default of `notifyOnNetworkStatusChange` from `false` to
 * `true`. `true` re-renders on every network status change, which sent the
 * admin home page into an infinite remount loop (see PR #19277), so every
 * client in the repo pins the Apollo 3 default.
 */
export const defaultWatchQueryOptions = { notifyOnNetworkStatusChange: false };

export function setupClient(options: { requestId: string }) {
  const state = coalesce(window?.kazoo?.state, { api: {}, application: {} });

  const apiClient = new ApolloClient({
    link: getHttpLink("/api", options.requestId),
    cache: new InMemoryCache({
      typePolicies: apiTypePolicies,
    }).restore(state.api),
    defaultOptions: { watchQuery: defaultWatchQueryOptions },
  });

  const client = new ApolloClient({
    link: getHttpLink("/graphql", options.requestId),
    cache: new InMemoryCache({
      typePolicies: applicationTypePolicies,
    }).restore(state.application),
    defaultOptions: { watchQuery: defaultWatchQueryOptions },
  });

  return {
    client,
    apiClient,
  };
}
